import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isNative } from '../utils/native';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Mail, ArrowRight, Loader2, Shield, Key, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const { user, loading: authLoading, loginWithOTP, verifyOTP, loginWithGoogle, setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');
  const [loginMethod, setLoginMethod] = useState('otp'); // 'otp' | 'password'
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    try { return !!localStorage.getItem('remember_email'); } catch { return false; }
  });

  // Pre-fill email from "Remember Me" persistence
  useEffect(() => {
    try {
      const saved = localStorage.getItem('remember_email');
      if (saved && !email) setEmail(saved);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: We intentionally do NOT auto-redirect already-authenticated users
  // away from /login. Visiting /login always shows the form so users can
  // switch accounts or re-authenticate after a stale session. The successful
  // login handler below performs the role-based redirect after fresh login.

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const result = await loginWithOTP(email, { mode: 'signin' });
      setDemoOtp(result.demo_otp || '');
      setStep('otp');
      toast.success('OTP sent! Check below for the code.');
    } catch (error) {
      // Brand-new email → redirect to the free-trial signup page (no shell accounts).
      if (error?.status === 404) {
        toast.info('No account found — sending you to the free trial signup.');
        navigate(`/free-trial${email ? `?email=${encodeURIComponent(email)}` : ''}`);
        return;
      }
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;

    setLoading(true);
    try {
      const userData = await verifyOTP(email, otp, { mode: 'signin' });
      toast.success('Login successful!');
      // Priority: new user must pick their role → then find/create a building → then dashboard.
      const needsRoleSelect = userData?.role_selected === false;
      const needsBuildingSetup = userData?.role === 'resident' && !userData?.hoa_id;
      // Honour ?next= query param from welcome-email deep-links.
      if (nextPath && !needsRoleSelect) {
        navigate(nextPath);
        return;
      }
      // On the native mobile app, every role lands on the resident dashboard.
      if (needsRoleSelect) {
        navigate('/select-role');
      } else if (isNative) {
        navigate(needsBuildingSetup ? '/find-building' : '/dashboard');
      } else if (userData?.role === 'platform_owner') {
        navigate('/platform');
      } else if (['managing_agent', 'agent_admin'].includes(userData?.role)) {
        navigate('/agent');
      } else if (['property_manager', 'trustee'].includes(userData?.role)) {
        navigate('/admin');
      } else if (needsBuildingSetup) {
        navigate('/find-building');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      if (error?.status === 404) {
        toast.info('No account found — sending you to the free trial signup.');
        navigate(`/free-trial${email ? `?email=${encodeURIComponent(email)}` : ''}`);
        return;
      }
      toast.error(error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const userData = await response.json();
        
        // Store session token for persistence
        if (userData.session_token) {
          localStorage.setItem('session_token', userData.session_token);
        }
        if (rememberMe) {
          localStorage.setItem('remember_email', email);
        } else {
          localStorage.removeItem('remember_email');
        }
        
        setUser(userData);
        toast.success('Login successful!');
        
        // Check if password change is required (first login with temp password)
        if (userData.require_password_change || userData.must_change_password) {
          toast.info('Please set a new password to continue');
          navigate('/first-login-password-change');
          return;
        }

        // Priority: role select → building setup → role-based dashboard.
        const needsRoleSelect = userData?.role_selected === false;
        const needsBuildingSetup = userData?.role === 'resident' && !userData?.hoa_id;
        // Redirect based on user role (native app forces resident dashboard)
        if (needsRoleSelect) {
          navigate('/select-role');
        } else if (isNative) {
          navigate(needsBuildingSetup ? '/find-building' : '/dashboard');
        } else if (userData?.role === 'platform_owner') {
          navigate('/platform');
        } else if (['managing_agent', 'agent_admin', 'super_agent', 'general_agent'].includes(userData?.role)) {
          navigate('/agent');
        } else if (['property_manager', 'trustee'].includes(userData?.role)) {
          navigate('/admin');
        } else if (needsBuildingSetup) {
          navigate('/find-building');
        } else {
          navigate('/dashboard');
        }
      } else {
        const error = await response.json();
        if (error.detail?.includes('OTP')) {
          toast.error('This account uses OTP login. Please switch to the OTP tab.');
          setLoginMethod('otp');
        } else {
          toast.error(error.detail || 'Invalid email or password');
        }
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Clickable to navigate home */}
        <div className="text-center mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/logo.png" alt="BodyCorp365" className="w-16 h-16 mx-auto mb-4 hover:opacity-80 transition-opacity" />
          <h1 className="text-2xl font-bold font-manrope text-slate-900 hover:text-slate-700">BodyCorp365</h1>
          <p className="text-slate-500 mt-1">Community Management Platform</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-manrope">
              {step === 'email' ? 'Welcome Back' : 'Enter Verification Code'}
            </CardTitle>
            <CardDescription>
              {step === 'email' 
                ? 'Sign in to manage your community' 
                : `We sent a code to ${email}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 'email' ? (
              <>
                {/* Login Method Tabs */}
                <Tabs value={loginMethod} onValueChange={setLoginMethod} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="otp" className="text-sm">
                      <Mail className="w-4 h-4 mr-2" />
                      OTP Login
                    </TabsTrigger>
                    <TabsTrigger value="password" className="text-sm">
                      <Lock className="w-4 h-4 mr-2" />
                      Agent Login
                    </TabsTrigger>
                  </TabsList>

                  {/* OTP Login Tab */}
                  <TabsContent value="otp">
                    <form onSubmit={handleSendOTP} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="pl-10 h-12"
                            data-testid="email-input"
                            required
                          />
                        </div>
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
                        disabled={loading}
                        data-testid="send-otp-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            Continue with Email
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Password Login Tab (for Agents) */}
                  <TabsContent value="password">
                    <form onSubmit={handlePasswordLogin} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            type="email"
                            placeholder="agent@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="pl-10 h-12"
                            data-testid="agent-email-input"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Password</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="pl-10 pr-10 h-12"
                            data-testid="password-input"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                            data-testid="toggle-show-password"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => {
                              setRememberMe(e.target.checked);
                              if (e.target.checked && email) {
                                localStorage.setItem('remember_email', email);
                              } else {
                                localStorage.removeItem('remember_email');
                              }
                            }}
                            className="rounded"
                            data-testid="remember-me"
                          />
                          Remember me
                        </label>
                        <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline" data-testid="forgot-password-link">
                          Forgot password?
                        </Link>
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full"
                        disabled={loading}
                        data-testid="password-login-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <User className="mr-2 w-5 h-5" />
                            Sign In as Agent
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-center text-slate-500 mt-2">
                        Agent accounts are created by Super Agents. Contact your administrator for access.
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-slate-500">or continue with</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-full border-slate-200"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={otp} 
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {/* OTP display for testing */}
                {demoOtp && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-sky-600 font-medium">Demo OTP (for testing):</p>
                    <p className="text-lg font-mono font-bold text-sky-700">{demoOtp}</p>
                  </div>
                )}

                <Button 
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Shield className="mr-2 w-5 h-5" />
                      Verify & Sign In
                    </>
                  )}
                </Button>

                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => { setStep('email'); setOtp(''); setDemoOtp(''); }}
                  data-testid="back-to-email-btn"
                >
                  Use a different email
                </Button>
              </div>
            )}

            {/* Invitation Code Option */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-center text-sm text-slate-500 mb-3">New resident?</p>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/invitation-register')}
                data-testid="invitation-code-btn"
              >
                <Key className="mr-2 w-4 h-4" />
                Enter Invitation Code
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-sky-600 hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-sky-600 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
